import { useContext, useEffect, useState, CSSProperties } from 'react'
import {
    dailyBudgetCalculator,
    estimatedCostPerView,
    estimatedStreamsCalculator,
    totalLengthCalculator,
} from '../../pages/MarketingPage/VideoAds/BudgetTab/utils'
import { SpendBudgetContext } from 'Hooks/BudgetTabSpendContext/BudgetTabSpendContext'
import getSymbolFromCurrency from 'currency-symbol-map'
import { CurrencyCodesType } from 'modules/Types'
import ButtonSymphony from 'elements/ButtonSymphony'
import CardSymphony from 'elements/CardSymphony'
import useCampaignBuilder, { CURRENT_BUDGET_TABS, CURRENT_DATE_SELECTION_TABS } from 'Hooks/useCampaignBuilder'
import { clsx } from 'modules/Utils'

interface Props {
    /** Optional additional CSS class name */
    className?: string;
    /** Whether the mobile button should be disabled */
    disableMobileButton: boolean;
    /** Function to call to finish the flow */
    finishFlow: () => void;
    /** Type of targeting being used */
    targetingType: string;
    /** Type of geography targeting being used */
    geographyType: string;
}



const RunningForCard = ({
    className,
    disableMobileButton,
    finishFlow,
    targetingType,
    geographyType
}: Props) => {
    const {
        budget: budgetFromCampaignStore,
        budgetMaximum,
        startDate: startDateFromCampaignStore,
        endDate: endDateFromCampaignStore,
        currentBudgetTab,
        currentDateSelectionTab,
        numberOfDays,
        adPlatform,
        getMinimumDailyBudget,
        getCampaignLength
    } = useCampaignBuilder();
    
    const budget = Number(budgetFromCampaignStore);
    const startDate = startDateFromCampaignStore ? new Date(startDateFromCampaignStore) : null;
    const endDate = endDateFromCampaignStore ? new Date(endDateFromCampaignStore) : null;

    const { currency } = useContext(SpendBudgetContext)

    const [estimatedStreams, setEstimatedStreams] = useState<string | null>(null)
    const [estimatedCostPerViewValue, setEstimatedCostPerView] = useState<string | null>(null)

    useEffect(() => {
        estimatedStreamsCalculator(budget, targetingType, geographyType, currency as CurrencyCodesType)
            .then((res) => setEstimatedStreams(res))

        estimatedCostPerView(targetingType, geographyType, currency as CurrencyCodesType)
            .then((res) => setEstimatedCostPerView(res))
    }, [currency, budget, targetingType, geographyType])
    

    /** Calculate the running duration based on date selection mode */
    const getRunningDuration = () => {
        if (currentDateSelectionTab === CURRENT_DATE_SELECTION_TABS.MAXIMUM_BUDGET) {
            // For maximum budget, calculate how long budget will last
            if (budget && budget > 0) {
                const dailySpend = currentBudgetTab === CURRENT_BUDGET_TABS.DAILY ? 
                    budget : 
                    getMinimumDailyBudget();
                
                const maxDays = Math.floor(Number(budgetMaximum) / dailySpend);
                return maxDays === 1 ? '1 day' : `${maxDays} days (estimated)`;
            }
        } else {
            // Use centralized campaign length calculation for date range and number of days
            const days = getCampaignLength();
            if (days > 0) {
                return days === 1 ? '1 day' : `${days} days`;
            }
        }
        
        return '...';
    };
    
    /** Calculate the daily spend based on budget type and date selection */
    const getDailySpend = () => {
        if (!budget || budget <= 0) return '...';
        
        if (currentBudgetTab === CURRENT_BUDGET_TABS.DAILY) {
            // If daily budget is selected, the daily spend is just the budget
            return budget.toFixed(2);
        } else if (currentDateSelectionTab === CURRENT_DATE_SELECTION_TABS.MAXIMUM_BUDGET) {
            // For maximum budget, assume minimum daily spend
            return getMinimumDailyBudget().toFixed(2);
        } else {
            // For lifetime budget, use centralized campaign length calculation
            const days = getCampaignLength();
            if (days > 0) {
                return (budget / days).toFixed(2);
            }
        }
        
        return '...';
    };
    
    /** Calculate the total budget/spend for the campaign */
    const getTotalBudget = () => {
        if (!budget || budget <= 0) return '...';
        
        if (currentBudgetTab === CURRENT_BUDGET_TABS.LIFETIME) {
            // If lifetime budget is selected, total budget is just the budget
            return budget.toFixed(2);
        } else if (currentDateSelectionTab === CURRENT_DATE_SELECTION_TABS.MAXIMUM_BUDGET) {
            // For maximum budget with daily budget option, use the maximum budget
            return budgetMaximum;
        } else {
            // For daily budget, use centralized campaign length calculation
            const days = getCampaignLength();
            if (days > 0) {
                return (budget * days).toFixed(2);
            }
        }
        
        return '...';
    };

    // Calculate display values
    const runningDuration = getRunningDuration();
    const dailySpend = getDailySpend();
    const totalBudget = getTotalBudget();

    // Get estimated values
    const costPerView = budget && budget > 0 ? estimatedCostPerViewValue : '...';
    const totalViews = budget && budget > 0 ? estimatedStreams : '...';

    // NOTE: render()
    return (
        <CardSymphony
            className={clsx('runningForCardComponent', className)}
            size="small"
            isPadding16
        >
            <div className="cardContentWrapper" style={sty.cardContent}>
                <div className="infoRow" style={sty.infoRow}>
                    <p className="infoLabel" style={sty.label}>Running for</p>
                    <p className="infoValue" style={sty.value}>{runningDuration}</p>
                </div>
                
                <div className="divider" style={sty.divider} />
                
                <div className="infoRow" style={sty.infoRow}>
                    <p className="infoLabel" style={sty.label}>Daily Spend</p>
                    <p className="infoValue" style={sty.value}>{`${getSymbolFromCurrency(currency)}${dailySpend}`}</p>
                </div>
                
                <div className="divider" style={sty.divider} />
                
                <div className="infoRow" style={sty.infoRow}>
                    <p className="infoLabel" style={sty.label}>Total Budget</p>
                    <p className="infoValue" style={sty.value}>{`${getSymbolFromCurrency(currency)}${totalBudget}`}</p>
                </div>
                
                <div className="divider" style={sty.divider} />
                
                {/* <div className="infoRow" style={sty.infoRow}>
                    <p className="infoLabel" style={sty.label}>Avg. Cost-per-View</p>
                    <p className="infoValue" style={sty.value}>{costPerView}</p>
                </div>
                
                <div className="divider" style={sty.divider} />
                
                <div className="infoRow" style={sty.infoRow}>
                    <p className="infoLabel" style={sty.label}>Estimated Total New Views</p>
                    <p className="infoValue" style={sty.value}>{totalViews}</p>
                </div> */}
            </div>
        </CardSymphony>
    )
}

const sty: Record<string, CSSProperties> = {
    cardContent: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        gap: 12
    },
    infoRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
    },
    label: {
        fontSize: 18,
        fontWeight: 500,
        margin: 0,
    },
    value: {
        fontSize: 16,
        color: '#505050',
        margin: 0,
        textAlign: 'right',
        whiteSpace: 'nowrap'
    },
    divider: {
        width: '100%',
        height: 1,
        backgroundColor: '#E0E0E0',
        margin: '8px 0'
    }
};

export default RunningForCard;

